/**
 * Sidebar — Operations rail layout on the Innu navy palette.
 * Custom classes (.brand-mark, .nav-item-active, .sidebar-shadow,
 * .scrollbar-thin) are defined in src/index.css.
 */
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { Bell, ChevronLeft, KanbanSquare, LayoutGrid, ListOrdered, Lock, PhoneCall } from 'lucide-react';
import { hasMinRole, useAuth, type InnuRole } from '@/lib/auth';
import { isEmbedded, postExit } from '@/lib/shell-bridge';

interface LinkDef {
  to: string;
  label: string;
  icon: typeof LayoutGrid;
  /** Hide this link below the given role. The matching ROUTE guard in
   *  App.tsx is what actually gates access; hiding a link is not security. */
  minRole?: InnuRole;
}

const workspaceLinks: LinkDef[] = [{ to: '/pipeline', label: 'Pipeline', icon: KanbanSquare }];

const adminLinks: LinkDef[] = [
  { to: '/settings/stages', label: 'Pipeline Stages', icon: ListOrdered },
  { to: '/settings/notifications', label: 'Notifications', icon: Bell },
  { to: '/settings/screening', label: 'Screening', icon: PhoneCall },
];

function navItemClass(isActive: boolean): string {
  const base =
    'relative flex min-h-10 items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] font-medium whitespace-nowrap transition-colors';
  return isActive
    ? `${base} nav-item-active text-white`
    : `${base} text-white/60 hover:bg-white/[0.06] hover:text-white/90`;
}

function userInitials(email?: string | null): string {
  if (!email) return '··';
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length === 0) return email.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

function userDisplayName(email?: string | null): string {
  if (!email) return 'no session';
  const local = email.split('@')[0] ?? email;
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((p) => p[0]!.toUpperCase() + p.slice(1))
    .join(' ');
}

function BrandLink({ children }: { children: ReactNode }) {
  const className =
    'flex items-center gap-2.5 rounded-lg -mx-1.5 px-1.5 py-1 min-h-10 hover:bg-white/[0.06] transition-colors text-left';
  // Inside the Innu Shell the brand link hands control back to the Shell
  // (innu:exit) instead of navigating the iframe to innu.app.
  if (isEmbedded()) {
    return (
      <button type="button" onClick={() => postExit()} title="Back to Innu" className={className}>
        {children}
      </button>
    );
  }
  return (
    <a href={SHELL_URL} target="_top" title="Back to Innu" className={className}>
      {children}
    </a>
  );
}

const SHELL_URL = import.meta.env.VITE_INNU_SHELL_URL ?? 'https://innu.app';

export function Sidebar({
  onNavigate,
  onCollapse,
}: { onNavigate?: () => void; onCollapse?: () => void } = {}) {
  const { user } = useAuth();
  const isAdmin = hasMinRole(user, 'company_admin');
  const visibleWorkspaceLinks = workspaceLinks.filter(
    (l) => !l.minRole || hasMinRole(user, l.minRole),
  );
  const role = user?.role ?? '';
  const initials = userInitials(user?.email);
  const displayName = userDisplayName(user?.email);

  return (
    <aside className="w-[200px] bg-navy text-white flex flex-col flex-shrink-0 sidebar-shadow">
      <div className="px-5 pt-5 pb-6 relative">
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
            className="absolute right-1.5 top-2.5 flex h-10 w-10 items-center justify-center rounded-md text-white/60 hover:bg-white/10 hover:text-white"
          >
            <ChevronLeft size={15} strokeWidth={2.2} aria-hidden />
          </button>
        )}
        <BrandLink>
          <div className="brand-mark w-8 h-8 rounded-lg bg-gradient-to-br from-navy to-navy-light ring-1 ring-white/15 flex items-center justify-center shrink-0">
            <LayoutGrid size={16} strokeWidth={2.5} className="text-white" aria-hidden />
          </div>
          <div>
            <div className="text-[15px] font-semibold tracking-tight leading-none font-brand">
              Recruiting
            </div>
            <div className="text-[12px] font-medium text-white/60 mt-0.5">by Innu</div>
          </div>
        </BrandLink>
      </div>

      <nav className="px-3 flex-1 overflow-y-auto scrollbar-thin">
        <div className="text-[12px] font-semibold text-white/60 px-3 mb-2">Workspace</div>
        {visibleWorkspaceLinks.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            className={({ isActive }) => navItemClass(isActive)}
          >
            <Icon size={16} strokeWidth={2} aria-hidden />
            <span>{label}</span>
          </NavLink>
        ))}

        {isAdmin && (
          <>
            <div className="mt-6 flex items-center gap-1.5 px-3 mb-2 text-[12px] font-semibold text-white/60">
              Admin
              <Lock size={11} strokeWidth={2.5} aria-hidden />
              <span className="sr-only">(company admins only)</span>
            </div>
            {adminLinks.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={onNavigate}
                className={({ isActive }) => navItemClass(isActive)}
              >
                <Icon size={16} strokeWidth={2} aria-hidden />
                <span>{label}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* User block */}
      <div className="px-3 pb-4 pt-3 border-t border-white/10">
        <div className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md">
          {/* Decorative: the name sits right next to it, so the initials are not announced. */}
          <div
            aria-hidden
            className="w-7 h-7 rounded-full bg-gradient-to-br from-navy-light to-accent flex items-center justify-center text-[12px] font-semibold text-white shrink-0"
          >
            {initials}
          </div>
          <div className="text-left flex-1 min-w-0">
            <div className="text-[12px] font-medium text-white truncate" title={user?.email ?? undefined}>
              {displayName}
            </div>
            {role && (
              <div className="mt-0.5 text-[12px] text-white/70 truncate" title={`Innu Auth role · ${role}`}>
                {role.replace(/_/g, ' ')}
              </div>
            )}
            {isEmbedded() && <div className="mt-0.5 text-[12px] text-white/60 truncate">via Innu Shell</div>}
          </div>
        </div>
      </div>
    </aside>
  );
}
